import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import StaffingModal from "./StaffingModal";
import { DataTable, ColumnDef } from "../../common/DataTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { AlertCircle } from "lucide-react";
import { MatchedStaff } from "@/types/staffing/matchedStaff";
import {
    getMatchedStaffForClient,
    getMatchedApplicantsForClient,
    getActivePostingForClient,
    createProcess,
    createPosting,
} from "@/services/staffing/Staffing";
import { useApiClient } from "@/context/ApiContext";
import { useStaffing } from "@/context/StaffingContext";
import { MATCH_SERVICE_TYPES, MatchServiceType, getViewFromServiceType, getServiceTypeFilterFromView, openStaffingGroupByReferenceId } from "@/utils/staffingUtil";
import PercentBar from "../../common/PercentBar";
import StaffContextMenu from "../contextMenus/StaffContextMenu";
import ApplicantContextMenu from "../contextMenus/ApplicantContextMenu";
import { Posting } from "@/types/staffing/posting";
import { CurrentUserContext } from "@/context/CurrentUserContext";

interface StaffMatchesModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientId: number;
    clientName: string | null;
    serviceType?: string;
}

const sharedColumns: ColumnDef<MatchedStaff>[] = [
    { key: 'percentMatch', header: 'Overall %', sortable: true, render: (row) => <PercentBar percent={row.percentMatch} /> },
    { key: 'percentMatchAvail', header: '% Avail', sortable: true, render: (row) => <PercentBar percent={row.percentMatchAvail} /> },
    { key: 'percentMatchDistance', header: '% Distance', sortable: true, render: (row) => <PercentBar percent={row.percentMatchDistance} /> },
    { key: 'percentMatchCredentials', header: '% Credentials', sortable: true, render: (row) => <PercentBar percent={row.percentMatchCredentials} /> },
    { key: 'role', header: 'Role', sortable: true },
    { key: 'city', header: 'City', sortable: true },
    { key: 'distance', header: 'Distance (mi)', sortable: true, render: (row) => row.distance.toFixed(1) },
];

const StaffMatchesModal = ({ open, onOpenChange, clientId, clientName, serviceType: propServiceType }: StaffMatchesModalProps) => {
    const apiClient = useApiClient();
    const { currentRegion, fetchPostings } = useStaffing();
    const { originalUser } = useContext(CurrentUserContext)
    const [selectedServiceType, setSelectedServiceType] = useState<MatchServiceType | null>(
        propServiceType ? propServiceType as MatchServiceType : null
    );
    const [matchedStaff, setMatchedStaff] = useState<MatchedStaff[]>([]);
    const [matchedApplicants, setMatchedApplicants] = useState<MatchedStaff[]>([]);
    const [staffLoading, setStaffLoading] = useState(false);
    const [applicantsLoading, setApplicantsLoading] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState<Set<string>>(new Set());
    const [selectedApplicants, setSelectedApplicants] = useState<Set<string>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<{ title: string; detail: string } | null>(null);

    const activeServiceType = propServiceType ?? selectedServiceType;

    const fetchMatches = useCallback(() => {
        if (!activeServiceType || !currentRegion) return;

        setStaffLoading(true);
        getMatchedStaffForClient(clientId, currentRegion, activeServiceType, 20, apiClient)
            .then(res => setMatchedStaff(res.data ?? []))
            .catch(err => console.error(err))
            .finally(() => setStaffLoading(false));

        setApplicantsLoading(true);
        getMatchedApplicantsForClient(clientId, currentRegion, activeServiceType, 20, apiClient)
            .then(res => setMatchedApplicants(res.data ?? []))
            .catch(err => console.error(err))
            .finally(() => setApplicantsLoading(false));
    }, [activeServiceType, currentRegion, clientId, apiClient]);

    useEffect(() => {
        if (!open) return;
        fetchMatches();
    }, [open, fetchMatches]);

    useEffect(() => {
        setSelectedStaff(new Set());
        setSelectedApplicants(new Set());
    }, [activeServiceType]);

    const getOrCreatePosting = async (): Promise<number> => {
        const res = await getActivePostingForClient(clientId, activeServiceType!, apiClient);
        const existing: Posting | undefined = res.data.value?.[0];
        if (existing) return existing.Id;
        await createPosting({ clientId, serviceType: activeServiceType!, telehealth: false }, apiClient);
        const fresh = await getActivePostingForClient(clientId, activeServiceType!, apiClient);
        return fresh.data.value[0].Id;
    };

    const addToProcess = async (identifiers: string[], applicant: boolean) => {
        if (!activeServiceType || identifiers.length === 0) return;
        setSubmitting(true);
        setError(null);
        try {
            const postingId = await getOrCreatePosting();
            const sourceList = applicant ? matchedApplicants : matchedStaff;
            let lastReferenceId: string | null = null;
            for (const staff of identifiers) {
                const match = sourceList.find(s => (applicant ? s.email : s.loginName) === staff);
                const res = await createProcess({
                    postingId,
                    staff,
                    source: 'Staffing Dashboard',
                    applicant,
                    author: originalUser.LoginName,
                    adHocGroup: {
                        clientId,
                        clientName: clientName ?? '',
                        staffFullName: match?.fullName ?? '',
                        staffRole: match?.role ?? '',
                        staffRegion: currentRegion ?? '',
                    }
                }, apiClient);
                lastReferenceId = res.data?.referenceId ?? lastReferenceId;
            }
            if (applicant) setSelectedApplicants(new Set());
            else setSelectedStaff(new Set());
            fetchMatches();
            const view = getViewFromServiceType(activeServiceType!);
            fetchPostings(view, {
                filter: `region eq '${currentRegion}' ${getServiceTypeFilterFromView(view)}`,
                expand: ['processes,client'],
                orderby: 'id desc'
            });
            if (lastReferenceId) openStaffingGroupByReferenceId(lastReferenceId);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const data = err.response.data;
                setError({ title: data?.title ?? 'Conflict', detail: data?.detail ?? 'A duplicate process entry already exists.' });
            } else {
                console.error(err);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const staffColumns = useMemo<ColumnDef<MatchedStaff>[]>(() => [
        {
            key: 'select',
            header: '',
            sticky: true,
            render: (row) => (
                <Checkbox
                    checked={selectedStaff.has(row.loginName)}
                    disabled={submitting}
                    onCheckedChange={(checked) => {
                        setSelectedStaff(prev => {
                            const next = new Set(prev);
                            checked ? next.add(row.loginName) : next.delete(row.loginName);
                            return next;
                        });
                    }}
                />
            )
        },
        { key: 'fullName', header: 'Name', sortable: true, sticky: true, render: (row) => <StaffContextMenu staff={row.loginName} staffName={row.fullName} isPosting={false} isMatch={true} /> },
        ...sharedColumns,
    ], [selectedStaff, submitting]);

    const applicantColumns = useMemo<ColumnDef<MatchedStaff>[]>(() => [
        {
            key: 'select',
            header: '',
            sticky: true,
            render: (row) => (
                <Checkbox
                    checked={selectedApplicants.has(row.email)}
                    disabled={submitting}
                    onCheckedChange={(checked) => {
                        setSelectedApplicants(prev => {
                            const next = new Set(prev);
                            checked ? next.add(row.email) : next.delete(row.email);
                            return next;
                        });
                    }}
                />
            )
        },
        { key: 'fullName', header: 'Name', sortable: true, sticky: true, render: (row) => <ApplicantContextMenu applicant={row.email} applicantName={row.fullName} isPosting={false} isMatch={true} /> },
        ...sharedColumns,
    ], [selectedApplicants, submitting]);

    const handleOpenChange = (val: boolean) => {
        if (!val) {
            if (!propServiceType) setSelectedServiceType(null);
            setSelectedStaff(new Set());
            setSelectedApplicants(new Set());
            setError(null);
        }
        onOpenChange(val);
    };

    return (
        <StaffingModal
            open={open}
            onOpenChange={handleOpenChange}
            title={`Matched Users — ${clientName}`}
            wide
        >
            <div className="flex flex-col gap-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{error.title}</AlertTitle>
                        <AlertDescription>{error.detail}</AlertDescription>
                    </Alert>
                )}
                {!propServiceType && (
                    <div className="flex items-center gap-3 mt-2 ml-1">
                        <Select
                            value={selectedServiceType ?? ''}
                            onValueChange={(val) => setSelectedServiceType(val as MatchServiceType)}
                        >
                            <SelectTrigger id="match-service-type" className="w-56">
                                <SelectValue placeholder="Select service type..." />
                            </SelectTrigger>
                            <SelectContent>
                                {MATCH_SERVICE_TYPES.map(type => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">Matched Staff</h3>
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            disabled={selectedStaff.size === 0 || submitting || !activeServiceType}
                            onClick={() => addToProcess(Array.from(selectedStaff), false)}
                        >
                            {submitting ? 'Adding...' : `Add ${selectedStaff.size} Staff to Process`}
                        </Button>
                    </div>
                    <DataTable
                        data={matchedStaff}
                        columns={staffColumns}
                        loading={staffLoading}
                    />
                </div>

                <div className="border-t" />

                <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">Matched Applicants</h3>
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            disabled={selectedApplicants.size === 0 || submitting || !activeServiceType}
                            onClick={() => addToProcess(Array.from(selectedApplicants), true)}
                        >
                            {submitting ? 'Adding...' : `Add ${selectedApplicants.size} Applicants to Process`}
                        </Button>
                    </div>
                    <DataTable
                        data={matchedApplicants}
                        columns={applicantColumns}
                        loading={applicantsLoading}
                    />
                </div>
            </div>
        </StaffingModal>
    );
};

export default StaffMatchesModal;
